import { api } from '@/lib/api'
import { API_PATHS } from '@/constants/api-paths'
import { normalizePlannerResourceLink, normalizePlannerResourceLinks } from '@/lib/planner-links'
import { resolveActiveServiceInstanceId } from '@/lib/service-instance'
import { useAuthStore } from '@/stores/auth-store'
import type { ApiResponse, PaginatedResponse, PlannerModel, PlannerResourceDefinition, PlannerResourceLink, PlannerWorkerCandidate } from '@/types'
import type { AssignableRole } from '@/services/org-user-admin'

type ListPlannerModelsParams = {
  service_instance_id?: string | number
  active?: boolean | number
  status?: string
  published?: boolean | number | string
  include_archived?: boolean | number | string
}

const resolvePlannerServiceInstanceId = (explicit?: string | number | null) => {
  const normalizedExplicit = explicit == null ? '' : String(explicit).trim()
  if (normalizedExplicit) {
    return normalizedExplicit
  }

  if (typeof window === 'undefined') {
    return null
  }

  const authState = useAuthStore.getState()
  return resolveActiveServiceInstanceId({
    search: window.location.search,
    post: authState.currentPost,
    currentBranch: authState.currentBranch,
    availableBranches: authState.availableBranches,
    storedServiceInstanceId: authState.serviceInstanceId,
    token: authState.accessToken,
  })
}

type ListPlannerResourceLinksParams = {
  service_instance_id?: string | number
  planner_model_id?: string | number
  planner_resource_id?: string | number
  product_id?: string | number
  product_variant_id?: string | number
  addon_item_id?: string | number
  order_id?: string | number
  serve_type_id?: string | number
  apply_to_all_orders?: boolean | number
}

type ListPlannerResourcesParams = {
  service_instance_id?: string | number
  planner_model_id?: string | number
  template_code?: string
  resource_kind?: string
  is_primary?: boolean | number
}

type ListPlannerWorkerCandidatesParams = {
  start_time?: string
  end_time?: string
}

type ListPlannerResourceRoleOptionsParams = {
  planner_model_id: string | number
}

type TriggerPlannerJobsForOrderPayload = {
  order_id?: string | number
  reference_id?: string
  trigger_mode?: 'auto' | 'manual'
}

type BuildPlanningContractForOrderParams = {
  order_id?: string | number
  reference_id?: string
}

type TriggerPlannerJobsForOrderResponse = {
  action: string
  job_id: string | number | null
  job_ids?: Array<string | number>
  job_ref?: string | null
  job_refs?: string[]
  matched_link_count: number
  assigned_worker_count?: number
  job_scope?: string
  refreshed?: boolean
  detail?: string
}

export type PlanningContractContext = {
  order_id?: string | number | null
  reference_id?: string | number | null
  service_instance_id?: string | number | null
  booking_service_instance?: {
    id?: string | number | null
    instance_id?: string | null
    name?: string | null
  } | null
  job_service_instance?: {
    id?: string | number | null
    instance_id?: string | null
    name?: string | null
  } | null
  matched_link_count?: number
  planner_models?: Array<{
    id?: string | number
    code?: string
    name?: string
    template_code?: string
    contract_version?: string
  }>
  serve_type_id?: string | number | null
  serve_type_label?: string | null
  delivery_time?: string | null
}

export type PlanningContractPreview = {
  contract_version?: string
  planning_mode?: string
  job_scope?: string
  job_trigger_status?: string | number | null
  job_creation_mode?: string
  priority_mode_enabled?: boolean
  priority_job_trigger_status?: string | number | null
  priority_job_creation_mode?: string
  strategy?: string
  fallback_strategy?: unknown[]
  work?: {
    work_items?: Array<{ name?: string; quantity?: number }>
  }
  effort_model?: unknown[]
  resource_requirements?: Array<{
    role?: string
    min?: number
    max?: number
    preferred?: number
  }>
  resource_capabilities?: unknown[]
  resource_dependencies?: unknown[]
  environment_constraints?: Record<string, unknown>
  time_constraints?: Record<string, unknown>
  context?: PlanningContractContext
}

export type PlannerModelPayload = {
  code: string
  name: string
  slug: string
  description?: string
  owner_service_instance?: string | number | null
  job_category?: string | number | null
  template_code?: string
  model_version?: number
  contract_version?: string
  planning_mode?: string
  job_scope?: string
  job_trigger_status?: string | number | null
  job_creation_mode?: string
  priority_mode_enabled?: boolean
  priority_job_trigger_status?: string | number | null
  priority_job_creation_mode?: string
  strategy?: string
  fallback_strategy?: unknown
  status?: string
  is_system?: boolean
  active?: boolean
  published_at?: string | null
  config_json?: Record<string, unknown>
}

export type PlannerResourceDefinitionPayload = {
  planner_model: string | number
  role_name?: string | number | null
  crew_template?: string | number | null
  role_code: string
  label: string
  resource_kind: string
  is_primary?: boolean
  min_count?: number
  max_count?: number
  preferred_count?: number | null
  ordering?: number
  dependency_rules_json?: Record<string, unknown>
  capability_schema_json?: Record<string, unknown>
}

export type PlannerModelConnectedPackage = {
  id: string | number
  label?: string | null
  product_id?: string | number | null
  product_name?: string | null
  connection_type: 'package_assignment' | 'planner_link'
}

export type PlannerModelConnectedAddon = {
  id: string | number
  name?: string | null
  addon_group_name?: string | null
  product_id?: string | number | null
  product_variant_id?: string | number | null
  product_variant_label?: string | null
  connection_type: 'planner_link'
}

export type PlannerModelConnectedCatalog = {
  planner_model_id: string | number
  packages: PlannerModelConnectedPackage[]
  addons: PlannerModelConnectedAddon[]
}

export type PlannerModelDeleteResult = {
  detail?: string
  cleanup?: {
    catalog_package_assignments_removed?: number
    planner_resource_links_removed?: number
    resource_definitions_removed?: number
    parameter_definitions_removed?: number
    planner_runs_detached?: number
  }
}

export type PlannerJobManagementInstance = {
  id?: string | number | null
  name?: string | null
  title?: string | null
  instance_id?: string | null
  service_id?: string | null
  identifier?: string | null
  url_identifier?: string | null
  parent_instance_id?: string | number | null
  status?: string | number | null
}

export type PlannerResourceLinkPayload = {
  service_instance: string | number
  planner_model: string | number
  planner_resource: string | number
  product?: string | number
  product_variant?: string | number
  addon_item?: string | number
  order?: string | number
  serve_type?: string | number
  job_trigger_status?: string | number
  job_creation_mode?: string
  apply_to_all_orders?: boolean
  quantity?: number
  scope?: string
  is_required?: boolean
  notes?: string
  config_json?: Record<string, unknown>
}

const unwrapData = <T>(payload: ApiResponse<T> | T): T => {
  if (payload && typeof payload === 'object' && 'data' in (payload as Record<string, unknown>)) {
    return (payload as ApiResponse<T>).data
  }

  return payload as T
}

const toPaged = <T>(payload: unknown): PaginatedResponse<T> => {
  if (payload && typeof payload === 'object' && 'results' in (payload as Record<string, unknown>)) {
    const paged = payload as PaginatedResponse<T>
    return {
      count: typeof paged.count === 'number' ? paged.count : Array.isArray(paged.results) ? paged.results.length : 0,
      next: paged.next ?? null,
      previous: paged.previous ?? null,
      results: Array.isArray(paged.results) ? paged.results : [],
    }
  }

  return {
    count: Array.isArray(payload) ? payload.length : 0,
    next: null,
    previous: null,
    results: Array.isArray(payload) ? payload as T[] : [],
  }
}

const isNotFoundResponse = (error: unknown) => {
  return (error as { response?: { status?: number } })?.response?.status === 404
}

export const resourcePlannerApi = {
  async listModels(params?: ListPlannerModelsParams): Promise<PlannerModel[]> {
    const serviceInstanceId = resolvePlannerServiceInstanceId(params?.service_instance_id)
    if (!serviceInstanceId) {
      return []
    }

    const response = await api.get<ApiResponse<PaginatedResponse<PlannerModel> | PlannerModel[]> | PaginatedResponse<PlannerModel> | PlannerModel[]>(
      API_PATHS.resourcePlanner.models,
      {
        params: {
          ...params,
          service_instance_id: serviceInstanceId,
        },
      },
    )
    const payload = unwrapData(response.data)
    return toPaged<PlannerModel>(payload).results
  },

  async createModel(payload: PlannerModelPayload): Promise<PlannerModel> {
    const response = await api.post<ApiResponse<PlannerModel> | PlannerModel>(API_PATHS.resourcePlanner.modelCreate, payload)
    return unwrapData(response.data)
  },

  async updateModel(id: string | number, payload: Partial<PlannerModelPayload>): Promise<PlannerModel> {
    const response = await api.patch<ApiResponse<PlannerModel> | PlannerModel>(API_PATHS.resourcePlanner.modelUpdate(id), payload)
    return unwrapData(response.data)
  },

  async deleteModel(
    id: string | number,
    params?: { service_instance_id?: string | number | null },
  ): Promise<PlannerModelDeleteResult> {
    const serviceInstanceId = resolvePlannerServiceInstanceId(params?.service_instance_id)
    const response = await api.delete<ApiResponse<PlannerModelDeleteResult> | PlannerModelDeleteResult>(
      API_PATHS.resourcePlanner.modelDelete(id),
      {
        params: serviceInstanceId ? { service_instance_id: serviceInstanceId } : undefined,
      },
    )
    return unwrapData(response.data)
  },

  async getModelConnectedCatalog(id: string | number): Promise<PlannerModelConnectedCatalog> {
    const response = await api.get<ApiResponse<PlannerModelConnectedCatalog> | PlannerModelConnectedCatalog>(
      API_PATHS.resourcePlanner.modelConnectedCatalog(id),
    )
    return unwrapData(response.data)
  },

  async listJobManagementInstances(serviceInstanceId?: string | number | null): Promise<PlannerJobManagementInstance[]> {
    const scopedServiceInstanceId = resolvePlannerServiceInstanceId(serviceInstanceId)
    if (!scopedServiceInstanceId) {
      return []
    }

    const response = await api.get<ApiResponse<PlannerJobManagementInstance[]> | PlannerJobManagementInstance[]>(
      API_PATHS.resourcePlanner.jobManagementInstances,
      {
        params: {
          service_instance_id: scopedServiceInstanceId,
        },
      },
    )
    return unwrapData(response.data)
  },

  async listResources(params?: ListPlannerResourcesParams): Promise<PlannerResourceDefinition[]> {
    const serviceInstanceId = resolvePlannerServiceInstanceId(params?.service_instance_id)
    if (!serviceInstanceId) {
      return []
    }

    const response = await api.get<ApiResponse<PaginatedResponse<PlannerResourceDefinition> | PlannerResourceDefinition[]> | PaginatedResponse<PlannerResourceDefinition> | PlannerResourceDefinition[]>(
      API_PATHS.resourcePlanner.resources,
      {
        params: {
          ...params,
          service_instance_id: serviceInstanceId,
        },
      },
    )
    const payload = unwrapData(response.data)
    return toPaged<PlannerResourceDefinition>(payload).results
  },

  async createResource(payload: PlannerResourceDefinitionPayload): Promise<PlannerResourceDefinition> {
    const response = await api.post<ApiResponse<PlannerResourceDefinition> | PlannerResourceDefinition>(API_PATHS.resourcePlanner.resourceCreate, payload)
    return unwrapData(response.data)
  },

  async updateResource(id: string | number, payload: Partial<PlannerResourceDefinitionPayload>): Promise<PlannerResourceDefinition> {
    const response = await api.patch<ApiResponse<PlannerResourceDefinition> | PlannerResourceDefinition>(API_PATHS.resourcePlanner.resourceUpdate(id), payload)
    return unwrapData(response.data)
  },

  async deleteResource(id: string | number): Promise<void> {
    await api.delete(API_PATHS.resourcePlanner.resourceDelete(id))
  },

  async listWorkerCandidates(resourceId: string | number, params?: ListPlannerWorkerCandidatesParams): Promise<PlannerWorkerCandidate[]> {
    const response = await api.get<ApiResponse<PlannerWorkerCandidate[]> | PlannerWorkerCandidate[]>(
      API_PATHS.resourcePlanner.resourceWorkerCandidates(resourceId),
      { params },
    )
    return unwrapData(response.data)
  },

  async listResourceRoleOptions(params: ListPlannerResourceRoleOptionsParams): Promise<AssignableRole[]> {
    const response = await api.get<ApiResponse<AssignableRole[]> | AssignableRole[]>(
      API_PATHS.resourcePlanner.resourceRoleOptions,
      { params },
    )
    return unwrapData(response.data)
  },

  async listResourceLinks(params?: ListPlannerResourceLinksParams): Promise<PlannerResourceLink[]> {
    const serviceInstanceId = resolvePlannerServiceInstanceId(params?.service_instance_id)
    if (!serviceInstanceId) {
      return []
    }

    const response = await api.get<ApiResponse<PaginatedResponse<PlannerResourceLink> | PlannerResourceLink[]> | PaginatedResponse<PlannerResourceLink> | PlannerResourceLink[]>(
      API_PATHS.resourcePlanner.serviceResourceLinks.list,
      {
        params: {
          ...params,
          service_instance_id: serviceInstanceId,
        },
      },
    )
    const payload = unwrapData(response.data)
    return normalizePlannerResourceLinks(toPaged<PlannerResourceLink>(payload).results)
  },

  async createResourceLink(payload: PlannerResourceLinkPayload): Promise<PlannerResourceLink> {
    const response = await api.post<ApiResponse<PlannerResourceLink> | PlannerResourceLink>(API_PATHS.resourcePlanner.serviceResourceLinks.create, payload)
    return normalizePlannerResourceLink(unwrapData(response.data))
  },

  async updateResourceLink(id: string | number, payload: Partial<PlannerResourceLinkPayload>): Promise<PlannerResourceLink> {
    const response = await api.patch<ApiResponse<PlannerResourceLink> | PlannerResourceLink>(API_PATHS.resourcePlanner.serviceResourceLinks.detail(id), payload)
    return normalizePlannerResourceLink(unwrapData(response.data))
  },

  async upsertResourceLink(id: string | number | null | undefined, payload: PlannerResourceLinkPayload): Promise<PlannerResourceLink> {
    if (id == null || String(id).trim() === '') {
      return this.createResourceLink(payload)
    }

    try {
      return await this.updateResourceLink(id, payload)
    } catch (error) {
      if (isNotFoundResponse(error)) {
        return this.createResourceLink(payload)
      }
      throw error
    }
  },

  async deleteResourceLink(id: string | number): Promise<void> {
    await api.delete(API_PATHS.resourcePlanner.serviceResourceLinks.delete(id))
  },

  async deleteResourceLinkIfExists(id: string | number): Promise<void> {
    try {
      await this.deleteResourceLink(id)
    } catch (error) {
      if (!isNotFoundResponse(error)) {
        throw error
      }
    }
  },

  async triggerJobsForOrder(payload: TriggerPlannerJobsForOrderPayload): Promise<TriggerPlannerJobsForOrderResponse> {
    const response = await api.post<ApiResponse<TriggerPlannerJobsForOrderResponse> | TriggerPlannerJobsForOrderResponse>(
      API_PATHS.resourcePlanner.serviceResourceLinks.triggerJobsForOrder,
      payload,
    )
    return unwrapData(response.data)
  },

  async buildContractForOrder(params: BuildPlanningContractForOrderParams): Promise<PlanningContractPreview> {
    const response = await api.get<ApiResponse<PlanningContractPreview> | PlanningContractPreview>(
      API_PATHS.resourcePlanner.serviceResourceLinks.buildContractForOrder,
      { params },
    )
    return unwrapData(response.data)
  },

  async listPlannerRunsForOrder(params: BuildPlanningContractForOrderParams): Promise<Array<Record<string, unknown>>> {
    const response = await api.get<ApiResponse<Array<Record<string, unknown>>> | Array<Record<string, unknown>>>(
      API_PATHS.resourcePlanner.serviceResourceLinks.plannerRunsForOrder,
      { params },
    )
    return unwrapData(response.data)
  },
}

export default resourcePlannerApi